import type { AlbumBackground, AlbumFont, AlbumFontPair, AlbumInk, AlbumMapStyle, AlbumPage, AlbumSlot, AlbumSlotFrame, AlbumStyles, AlbumTheme, AlbumVeil } from '../types';

export interface AlbumLook {
  theme: AlbumTheme;
  background: AlbumBackground;
  ink: AlbumInk;
  accent: string;
  /** The map palette on this page: the theme's, on this page's paper and ink. */
  map: AlbumMapStyle;
  photo: { id: string; veil: AlbumVeil } | null;
}

/**
 * ADR-0048: what a page looks like — its own background or its theme's, and
 * the ink that reads on it. An id this build doesn't know falls back to the
 * default instead of failing: the server can add themes before the app learns them.
 */
export function albumLook(styles: AlbumStyles, themeId: string, page: Pick<AlbumPage, 'background' | 'backgroundPhotoId' | 'backgroundAlbumPhotoId' | 'backgroundVeil'>): AlbumLook {
  const theme = styles.themes.find(t => t.id === themeId)
    ?? styles.themes.find(t => t.id === styles.defaultTheme)
    ?? styles.themes[0];
  const byId = (id: string | null) => (id ? styles.backgrounds.find(b => b.id === id) : undefined);
  const background = byId(page.background) ?? byId(theme.background) ?? styles.backgrounds[0];
  const photoId = page.backgroundPhotoId ?? page.backgroundAlbumPhotoId;
  const photo = photoId ? { id: photoId, veil: page.backgroundVeil } : null;
  // On a photo, the veil decides: a dark veil takes light ink.
  const ink = styles.inks[photo ? (photo.veil === 'dark' ? 'light' : 'dark') : background.ink];
  return {
    theme,
    background,
    ink,
    accent: theme.accent,
    // The map's legend sits on its own paper rectangle, never on the photo: it
    // takes the paper's ink, or a dark veil leaves it white on cream.
    map: { ...theme.map, paper: background.color, ink: styles.inks[background.ink].text },
    photo,
  };
}

/** ADR-0048: the album's typeface pair, or the default one for an id this build doesn't know. */
export function albumFonts(styles: AlbumStyles, fontPairId: string): AlbumFontPair {
  return styles.fontPairs.find(f => f.id === fontPairId)
    ?? styles.fontPairs.find(f => f.id === styles.defaultFontPair)
    ?? styles.fontPairs[0];
}

/**
 * ADR-0053: the font a text box is drawn in — its own, or the pair's for its
 * kind. An id this build doesn't know reads in the pair's, never fails.
 */
export function slotFont(styles: AlbumStyles, pair: AlbumFontPair, kind: 'TITLE' | 'TEXT', fontId: string | null): AlbumFont {
  return (fontId ? styles.fonts.find(f => f.id === fontId) : undefined) ?? (kind === 'TITLE' ? pair.title : pair.body);
}

/**
 * The fonts a box offers: the same pairs the album picks from, each giving its
 * cut for the box's kind. Pairs sharing that cut (three bodies are Inter) are
 * one choice, named after all of them. A box already in a cut no pair gives
 * keeps it on the list, so the choice is never hidden.
 */
export function boxFontChoices(styles: AlbumStyles, kind: 'TITLE' | 'TEXT', current: string | null): { id: string; pairs: string[]; font: AlbumFont }[] {
  const choices: { id: string; pairs: string[]; font: AlbumFont }[] = [];
  for (const pair of styles.fontPairs) {
    const cut = kind === 'TITLE' ? pair.title : pair.body;
    const font = styles.fonts.find(f => f.family === cut.family && f.weight === cut.weight && f.italic === cut.italic);
    if (!font) continue;
    const same = choices.find(c => c.id === font.id);
    if (same) same.pairs.push(pair.id);
    else choices.push({ id: font.id, pairs: [pair.id], font });
  }
  const own = current && !choices.some(c => c.id === current) ? styles.fonts.find(f => f.id === current) : undefined;
  if (own) choices.push({ id: own.id, pairs: [], font: own });
  return choices;
}

const basePt =(fontPt: Record<'TITLE' | 'TEXT', number>, frame: AlbumSlotFrame) => frame.pt ?? fontPt[frame.kind === 'TITLE' ? 'TITLE' : 'TEXT'];

/** ADR-0053: the size a box's text is drawn at, in pt — its own, or its S/M/L step over the frame's size. */
export function slotPt(styles: AlbumStyles, fontPt: Record<'TITLE' | 'TEXT', number>, frame: AlbumSlotFrame, slot: Pick<AlbumSlot, 'textPt' | 'textSize'> | undefined): number {
  return slot?.textPt ?? basePt(fontPt, frame) * (styles.textSizeScale[slot?.textSize ?? 'M'] ?? 1);
}

const MM_PER_PT = 25.4 / 72;

/**
 * The server's rule (`maxCharsAt`, ADR-0053): by area (scale²); a title grown
 * past its size also takes what fits its box at that size, up to its cap.
 */
export function maxCharsAt(fontPt: Record<'TITLE' | 'TEXT', number>, frame: AlbumSlotFrame, pt: number): number {
  const maxChars = frame.maxChars ?? 0;
  const scale = pt / basePt(fontPt, frame);
  const byArea = Math.max(10, Math.round(maxChars / (scale * scale)));
  if (frame.kind !== 'TITLE') return byArea;
  const em = pt * MM_PER_PT;
  const fits = Math.floor(frame.w / (em * 0.5)) * Math.max(1, Math.floor(frame.h / (em * 1.25)));
  return Math.max(byArea, Math.min(maxChars, fits));
}
